import { StoreError } from '@/types/storeError.interface';
import { IApplication, IndexedAttestations } from '@/typings';
import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import { RootState } from '../store';

interface AttestationState {
  /** True while a foreground fetch is in flight. Drives the QuestionsPage skeleton. */
  loading: boolean;
  error: StoreError | null;
  attestations: IndexedAttestations;
}

type AttestationSummary = {
  requiredFieldsCount: number;
  fieldsCount: number;
  pendingRequiredFieldsCount: number;
  pendingFieldsCount: number;
  restrictionViolationsCount: number;
  completedPercentage: number;
};

const initialState: AttestationState = {
  attestations: {},
  loading: false,
  error: null,
};

export const attestationSlice = createSlice({
  name: 'attestation',
  initialState,
  reducers: {
    attestationLoading: state => {
      state.loading = true;
    },
    fetchAttestationSuccess: (
      state,
      action: PayloadAction<{
        attestations: IndexedAttestations;
        background?: boolean;
      }>,
    ) => {
      state.error = null;

      // A background refresh (the reload-required fetch after a save) must not clear a
      // skeleton some other foreground fetch on this page is still waiting on.
      if (!action.payload.background) {
        state.loading = false;
      }

      state.attestations = {
        ...state.attestations,
        ...action.payload.attestations,
      };
    },
    // Deliberately leaves `loading` alone: this runs on every save purely to refresh the
    // header's progress counters, and clearing the flag here would drop a skeleton the
    // foreground fetch that raised it is still waiting on.
    fetchAttestationSummarySuccess: (
      state,
      action: PayloadAction<{
        applicationId: IApplication['id'];
        summary: AttestationSummary;
      }>,
    ) => {
      state.error = null;
      state.attestations[action.payload.applicationId] = {
        ...state.attestations[action.payload.applicationId],
        ...action.payload.summary,
      };
    },
    fetchAttestationFailure: (state, action: PayloadAction<StoreError>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  attestationLoading,
  fetchAttestationSuccess,
  fetchAttestationSummarySuccess,
  fetchAttestationFailure,
} = attestationSlice.actions;

export const selectAttestation = (state: RootState) =>
  state[attestationSlice.name] as typeof initialState;
